/**
 * ModelWizard - Tabs-based model registration editor
 */
import React, { useState, useEffect, useRef } from 'react';
import { Alert, Tabs, Button, message, Modal, Spin, Tag, Typography, Grid } from 'antd';
import { SaveOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { WizardProvider, useWizard } from '../components/ModelWizard/WizardContext';
import Step1BasicInfo from '../components/ModelWizard/Step1BasicInfo';
import Step2DrugPopulation from '../components/ModelWizard/Step2DrugPopulation';
import Step3ModelStructure from '../components/ModelWizard/Step3ModelStructure';
import Step4BoundaryConditions from '../components/ModelWizard/Step4BoundaryConditions';
import Step5Covariates from '../components/ModelWizard/Step5Covariates';
import Step6Reliability from '../components/ModelWizard/Step6Reliability';
import Step7ClinicalApplication from '../components/ModelWizard/Step7ClinicalApplication';

const EMPTY_STEP_DATA = {};
const { Text } = Typography;

const tabs = [
  { key: '1', label: '基本信息', component: Step1BasicInfo },
  { key: '2', label: '纳排标准', component: Step2DrugPopulation },
  { key: '3', label: '模型结构', component: Step3ModelStructure },
  { key: '4', label: '边界条件', component: Step4BoundaryConditions },
  { key: '5', label: '协变量', component: Step5Covariates },
  { key: '6', label: '可靠性', component: Step6Reliability },
  { key: '7', label: '临床应用', component: Step7ClinicalApplication },
];

interface ModelWizardContentProps {
  onClose: () => void;
  editModelId?: string;
  onSubmitted?: (modelId: string) => void;
}

export const ModelWizardContent: React.FC<ModelWizardContentProps> = ({ onClose, editModelId, onSubmitted }) => {
  const screens = Grid.useBreakpoint();
  const {
    currentStep,
    setCurrentStep,
    stepData,
    updateStepData,
    saveDraft,
    submitModel,
    loadWizardData,
    loading,
    modelId,
    isDraft,
  } = useWizard();

  const [activeTab, setActiveTab] = useState<string>('1');
  const stepFormRef = useRef<any>(null);

  useEffect(() => {
    const targetTab = String(currentStep + 1);
    setActiveTab(prev => (prev === targetTab ? prev : targetTab));
  }, [currentStep]);

  useEffect(() => {
    if (editModelId) {
      loadWizardData(editModelId);
    }
  }, [editModelId]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setCurrentStep(parseInt(key, 10) - 1);
  };

  const getCurrentStepPayload = async () => {
    const stepNum = parseInt(activeTab, 10);
    let currentData = stepData[stepNum] || {};

    if (stepFormRef.current) {
      await stepFormRef.current.validateFields();
      currentData = {
        ...currentData,
        ...stepFormRef.current.getFieldsValue(true),
      };
    }

    return { stepNum, currentData };
  };

  const handleSaveCurrentTab = async () => {
    try {
      const { stepNum, currentData } = await getCurrentStepPayload();
      if (stepNum === 5 && stepFormRef.current?.saveBindings) {
        await stepFormRef.current.saveBindings();
        message.success('协变量绑定已保存');
        return { stepNum, savedData: currentData };
      }
      const savedData = await saveDraft(stepNum, currentData);
      return { stepNum, savedData };
    } catch (error: any) {
      if (error?.errorFields?.length) {
        message.warning('请先补全当前页的必填项');
      }
      throw error;
    }
  };

  const handleSaveClick = async () => {
    try {
      await handleSaveCurrentTab();
    } catch (error) {
      // Validation and API errors are already surfaced to the user
    }
  };

  const handleSubmit = async () => {
    if (!isDraft) {
      try {
        await handleSaveCurrentTab();
      } catch (error) {
        // Validation and API errors are already surfaced to the user
      }
      return;
    }
    Modal.confirm({
      title: '确认完成资料登记',
      content: '模型将以停用状态进入模型库。模型包、资格、审核和发布后续完成；临床证据独立补充。',
      okText: '完成登记',
      cancelText: '继续编辑',
      onOk: async () => {
        try {
          const { stepNum, savedData } = await handleSaveCurrentTab();
          await submitModel({ ...stepData, [stepNum]: savedData });
          if (modelId) onSubmitted?.(modelId);
          onClose();
        } catch (error) {
          // Validation and API errors are already surfaced to the user
        }
      },
    });
  };

  return (
    <Spin spinning={loading}>
      <div style={{ minHeight: '100%' }}>
        <div className="model-wizard__toolbar">
          <div className="model-wizard__status">
            <Text type="secondary">模型 ID</Text>
            <Text strong>{modelId || '保存后生成'}</Text>
            <Tag color={!modelId ? 'default' : isDraft ? 'orange' : 'blue'}>
              {!modelId ? '未保存' : isDraft ? '草稿' : '已提交'}
            </Tag>
            <Text type="secondary">第 {activeTab} / 7 项</Text>
          </div>
          <div className="model-wizard__actions">
            <Button aria-label="Save current step" icon={<SaveOutlined />} onClick={handleSaveClick}>
              保存当前页
            </Button>
            <Button aria-label="Submit model" type="primary" icon={<CheckOutlined />} onClick={handleSubmit}>
              {isDraft ? '完成资料登记' : '保存资料更新'}
            </Button>
            <Button aria-label="Close wizard" icon={<CloseOutlined />} onClick={onClose}>
              关闭
            </Button>
          </div>
        </div>

        {isDraft ? (
          <Alert
            className="model-wizard__notice"
            type="info"
            showIcon
            message="模型草稿"
            description="逐项保存；完成登记后进入模型库并保持停用。"
          />
        ) : null}

        <Tabs
          className="drug-editor-tabs"
          activeKey={activeTab}
          onChange={handleTabChange}
          tabPosition={screens.lg ? 'left' : 'top'}
          size="small"
          destroyOnHidden={false}
          items={tabs.map(tab => {
            const TabComponent = tab.component;
            const tabStepNum = parseInt(tab.key, 10);
            const tabData = stepData[tabStepNum] ?? EMPTY_STEP_DATA;

            return {
              key: tab.key,
              label: tab.label,
              children: (
                <div className="model-wizard__content">
                  <TabComponent
                    data={tabData}
                    onChange={(data: any) => {
                      if (tab.key === activeTab) {
                        updateStepData(tabStepNum, data);
                      }
                    }}
                    formRef={tab.key === activeTab && (tabStepNum <= 2 || tabStepNum === 5) ? stepFormRef : undefined}
                  />
                </div>
              ),
            };
          })}
        />
      </div>
    </Spin>
  );
};

interface ModelWizardProps {
  visible: boolean;
  onClose: () => void;
  editModelId?: string;
  onSubmitted?: (modelId: string) => void;
}

const ModelWizard: React.FC<ModelWizardProps> = ({ visible, onClose, editModelId, onSubmitted }) => {
  return (
    <Modal
      className="drug-editor-modal model-wizard-modal"
      title={editModelId ? `编辑模型：${editModelId}` : '新增模型'}
      width="min(96vw, 1440px)"
      open={visible}
      onCancel={onClose}
      footer={null}
      style={{ top: 12 }}
      destroyOnHidden
      styles={{
        body: {
          maxHeight: '84vh',
          overflow: 'hidden',
          paddingBottom: 8,
        },
      }}
    >
      <WizardProvider>
        <ModelWizardContent onClose={onClose} editModelId={editModelId} onSubmitted={onSubmitted} />
      </WizardProvider>
    </Modal>
  );
};

export default ModelWizard;
